/** The images this browser tab uploaded for an `uploaded`-staging image field (ImageWidget,
 *  plan D3 / §9.4), by Image id: what the id in the draft shows until the save — the
 *  downsized image from the upload's CDN URL, its file name and size. The draft holds only
 *  the id.
 *
 *  Mirrored to sessionStorage (`console:staged-uploads`), beside the draft's own mirror, so a
 *  reload that restores the draft still shows the picked image rather than "Uploaded image
 *  #id". Every storage access is guarded — storage can be absent, blocked or full — and the
 *  page works without it (the in-memory map, then the id). Only the newest uploads are kept;
 *  a copy that does not parse is ignored. */
import type { StoredImage } from './ImageWidget';

export interface UploadedImage {
  image: StoredImage;
  /** The file's size in bytes. */
  size: number;
}

export const UPLOADED_IMAGES_STORAGE_KEY = 'console:staged-uploads';
/** Bump when the stored shape changes; an older copy is ignored. */
const VERSION = 1;
/** The newest this many are kept — far more than one page session picks. */
export const UPLOADED_IMAGES_MAX = 50;

const memory = new Map<number, UploadedImage>();

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null; // SecurityError: storage blocked for this origin
  }
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const textOrNull = (v: unknown): v is string | null => v === null || typeof v === 'string';

function asUploaded(v: unknown): UploadedImage | null {
  if (!isObject(v) || typeof v.size !== 'number' || !isObject(v.image)) return null;
  const { id, url, p, s, name } = v.image;
  if (typeof id !== 'number' || !textOrNull(url) || !textOrNull(p) || !textOrNull(s)) return null;
  if (name !== undefined && !textOrNull(name)) return null;
  return { image: { id, url, p, s, ...(name !== undefined ? { name } : {}) }, size: v.size };
}

/** The stored uploads, oldest first; [] when there are none or the copy does not parse. */
function readStored(store: Storage): Array<[number, UploadedImage]> {
  let raw: string | null;
  try {
    raw = store.getItem(UPLOADED_IMAGES_STORAGE_KEY);
  } catch {
    return [];
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed) || parsed.v !== VERSION || !Array.isArray(parsed.items)) return [];
    const out: Array<[number, UploadedImage]> = [];
    for (const item of parsed.items as unknown[]) {
      if (!Array.isArray(item) || typeof item[0] !== 'number') continue;
      const entry = asUploaded(item[1]);
      if (entry) out.push([item[0], entry]);
    }
    return out;
  } catch {
    return [];
  }
}

/** Remember an upload: for this page, and for a reload of this tab. */
export function rememberUpload(id: number, entry: UploadedImage): void {
  memory.set(id, entry);
  const store = storage();
  if (!store) return;
  const items = readStored(store).filter(([k]) => k !== id);
  items.push([id, entry]);
  try {
    store.setItem(
      UPLOADED_IMAGES_STORAGE_KEY,
      JSON.stringify({ v: VERSION, items: items.slice(-UPLOADED_IMAGES_MAX) }),
    );
  } catch {
    // Quota or blocked storage: this page still has it in memory.
  }
}

/** An upload of this tab by its Image id — this page's, else one a reload left in storage. */
export function uploadedImage(id: number): UploadedImage | undefined {
  const known = memory.get(id);
  if (known) return known;
  const store = storage();
  const found = store ? readStored(store).find(([k]) => k === id)?.[1] : undefined;
  if (found) memory.set(id, found);
  return found;
}
